/**
 * The unit sheet's **loadout** (`unit-sheet-redesign` Phase 4) as data: the groups and rows the
 * sheet prints under its statuses, one pure derivation per kind, so the row set and every state pill
 * are provable without a renderer.
 *
 * Numbers come from the frozen base (a weapon's damage and range, a system's destroyed flag) and
 * rule text from the compendium, per `decisions/0122`. Every state pill reads what the command menu
 * reads: a weapon's through {@link classifySkirmishCard} (the verdict `weaponBlockShort` words), and
 * a feature's, a system's, the core's and a talent's through `featureState`, keyed by the affordance
 * ids the spawn freeze minted. Nothing here re-derives a wall.
 */
import type { Compendium } from "@lancer-vtt/compendium";
import { type Affordance, type Combatant, type EncounterState, type FrozenNpcFeature, type FrozenSystem, type WeaponProfile, isItemDestroyed, weaponInstanceKey } from "@lancer-vtt/domain";
import { type FeatureState, type LoadedOverlay, clauseCapState, featureState, frameEffectEntries, frameTraitEffectEntries } from "@lancer-vtt/rules";
import { classifySkirmishCard } from "./combatWindows/chooserClassify";
import type { ActionDraft } from "./combatInspector";
import type { Ref } from "../../routes/compendium/compendiumData";
import { npcWeaponLine, tagRuleText, usesLeftLine } from "./commandMenu";
import { isNpcFeatureDestroyed, isNpcFeatureGmRuled, npcFeatureKindTag, orderedNpcFeatures } from "./npcFeatures";
import { formatTagChip, formatWeaponDamage, formatWeaponRange } from "./weaponFormat";

/** A chip on a row's tag line. `tag` is a plain tag; the rest are state pills. */
export type LoadoutChip = { label: string; tone: "tag" | "on" | "off" | "warn" | "fiction"; title?: string };

export type LoadoutRow = {
  key: string;
  /** The kind tag: `WPN`, `SYS`, `CORE`, `TAL`, `CB`, `TRT`, `TECH`, `RCT`, `ABOUT`. */
  kind: string;
  name: string;
  /** The right-hand line: range and damage for a weapon, SP for a system, the activation for a core. */
  meta?: string;
  chips: LoadoutChip[];
  /** Rule text (an HTML fragment for `RulesText`), toggled inline by a click on the row. */
  text?: string;
  destroyed?: boolean;
  /** The engine does not carry this NPC feature or weapon out; the GM rules it at the table (`isNpcFeatureGmRuled`). */
  gmRuled?: boolean;
  /** The aim whose reach the board paints while the row is hovered (range-hover); absent for a row with no reach. */
  preview?: ActionDraft;
  /** The BUILD posture's mount label (`MAIN MOUNT`): the row opens a new sub-section under its group's head when this changes. */
  section?: string;
  /** A slot with no weapon in it, a Flex slot a Main weapon locks, or a mod under its weapon (BUILD posture). */
  variant?: "empty" | "locked" | "sub";
  /** The COMPENDIUM record the row names: the host may open it from the row. */
  ref?: Ref;
  /** A rollable row (a skill trigger): the caption and bonus its roll button seeds into DICE. */
  roll?: { label: string; bonus: number };
};

export type LoadoutGroup = {
  id: string;
  title: string;
  rows: LoadoutRow[];
  /** A budget the group spends against, shown as `SP used / max` and a bar in its head (BUILD's SYSTEMS). */
  meter?: { label: string; used: number; max: number };
  /** What the head says when the group has no rows (BUILD's sections render even when empty). */
  empty?: string;
  /** BUILD's EDIT: outstanding build work in this section (`buildSignals`), painted on the head with the reasons as its title. */
  flag?: SectionFlag;
  /** BUILD's EDIT: the FORGE anchor a jump lands on (`data-build-anchor`, `routes/home/forgeGuide.ts`). */
  anchor?: string;
};

/** A build section's outstanding work: `todo` is room left to spend, `warn` an invalid hold. The title carries the advisor's reasons, one per line. */
export type SectionFlag = { severity: "todo" | "warn"; title: string };

/** An NPC's loadout, with its reaction features kept apart for the REACTIONS group. */
export type NpcLoadout = { groups: LoadoutGroup[]; reactions: LoadoutRow[] };

const DESTROYED: LoadoutChip = { label: "DESTROYED", tone: "warn" };

/** The tags a state pill already states, left off the row's plain tags so nothing prints twice. */
const PILL_TAGS: ReadonlySet<string> = new Set(["tg_recharge", "tg_limited"]);

function tagChips(tags: readonly { id: string; val?: number }[] | undefined): LoadoutChip[] {
  return (tags ?? []).filter((t) => !PILL_TAGS.has(t.id)).map((t) => ({ label: formatTagChip(t.id, t.val), tone: "tag" }));
}

/**
 * The pills a {@link FeatureState} states: `DESTROYED`; `RECHARGE 5+` with `CHARGED` or
 * `DISCHARGED`; a round rate as `1/ROUND` and, spent, `1/ROUND · SPENT`; and `2 of 3 left` for a
 * Limited pool or a scene rate. A record carrying a round rate and a pool shows both
 * (`decisions/0202`: Drop Bomb's `1/ROUND` and `2 OF 3 LEFT`).
 */
export function featureChips(fs: FeatureState): LoadoutChip[] {
  if (fs.destroyed) return [DESTROYED];
  const chips: LoadoutChip[] = [];
  if (fs.recharge) {
    chips.push({ label: `RECHARGE ${fs.recharge.on}+`, tone: "tag" });
    chips.push(fs.recharge.charged ? { label: "CHARGED", tone: "on" } : { label: "DISCHARGED", tone: "off" });
  }
  if (fs.roundRate !== undefined) {
    const cap = `${fs.roundRate}/ROUND`;
    chips.push(fs.usedThisRound ? { label: `${cap} · SPENT`, tone: "off" } : { label: cap, tone: "tag" });
  }
  if (fs.uses && fs.uses.per === "scene") {
    chips.push({ label: usesLeftLine(fs.uses).toUpperCase(), tone: fs.uses.left > 0 ? "on" : "off" });
  }
  return chips;
}

/** The state of the first of `keys` that carries any (a recharge, a count or a destroyed flag). */
function firstState(c: Combatant, state: EncounterState, keys: readonly string[]): FeatureState {
  for (const key of keys) {
    const fs = featureState({ combatant: c, state, key });
    if (fs.destroyed || fs.recharge || fs.uses) return fs;
  }
  return { destroyed: false };
}

/** A row's state with its clause cap (D12) beside it, when the row's affordances report no round rate of their own: Aggression's "1/ROUND · SPENT". */
function withClauseCap(fs: FeatureState, cap: FeatureState | undefined): FeatureState {
  return cap === undefined || fs.roundRate !== undefined ? fs : { ...fs, roundRate: cap.roundRate!, ...(cap.usedThisRound ? { usedThisRound: true } : {}) };
}

/** The frozen affordance keys an item or entity minted (`ms_x:0`, `ms_x:1`, or the bare effect id). */
function affordanceKeysOf(affordances: readonly Affordance[] | undefined, id: string): string[] {
  return (affordances ?? []).filter((a) => (a.source.kind === "granted" ? a.source.itemId === id : a.source.entityId === id)).map((a) => a.key);
}

/**
 * A weapon's state pills, off the menu's own verdict ({@link classifySkirmishCard}, read as a Barrage
 * pick so a Superheavy is not "blocked") plus `featureState`'s recharge and charge counts. The
 * Ordnance and Superheavy verdicts are firing windows, not the weapon's state, so they add nothing.
 */
export function weaponChips(args: { weapon: WeaponProfile; combatant: Combatant; state: EncounterState }): LoadoutChip[] {
  const { weapon: w, combatant: c, state } = args;
  const verdict = classifySkirmishCard({ weapon: w, combatant: c, state, isOverwatch: false, barrage: true });
  if (verdict === "destroyed") return [DESTROYED];
  const fs = featureState({ combatant: c, state, key: weaponInstanceKey(w) });
  const chips = featureChips(fs);
  if (verdict === "unloaded") chips.unshift({ label: "UNLOADED", tone: "warn" });
  else if (w.loading && verdict !== "discharged") chips.unshift({ label: "LOADED", tone: "on" });
  return chips;
}

/** A weapon row's hover preview: a Skirmish with that weapon, whose reach the board washes. */
function weaponPreview(c: { id: string }, w: WeaponProfile): ActionDraft {
  return { attackerId: c.id, mode: "skirmish", weaponId: weaponInstanceKey(w), profileId: "", accuracy: 0 };
}

/**
 * A system's, gear's or feature's hover preview: the first of its granted actions (`keys`) with a
 * reach, as a tech attack (Sensors) or an activation (its frozen `actionRanges` reach). `undefined`
 * when none has one: a self-only system, a trait.
 */
function grantedPreview(c: Combatant, keys: readonly string[]): ActionDraft | undefined {
  for (const key of keys) {
    if (c.base.techAttackActions?.[key]) return { attackerId: c.id, mode: "tech_attack", actionId: key, accuracy: 0 };
    if (c.base.actionRanges?.[key]?.length) return { attackerId: c.id, mode: "activate", actionId: key };
  }
  return undefined;
}

const withPreview = (preview: ActionDraft | undefined): { preview?: ActionDraft } => (preview ? { preview } : {});

/** A compendium entry's rule prose: its `effect`, then its `on_attack` / `on_hit` / `on_crit` riders, as one HTML fragment. */
function entryProse(entry: unknown): string {
  const e = (entry ?? {}) as Record<string, unknown>;
  const parts: string[] = [];
  if (typeof e["effect"] === "string" && e["effect"].trim()) parts.push(e["effect"]);
  for (const [field, label] of [
    ["on_attack", "ON ATTACK"],
    ["on_hit", "ON HIT"],
    ["on_crit", "ON CRIT"],
  ] as const) {
    // v3 NPC weapons carry a rider either as prose or as `{ detail, add_status }` (the Nail Gun).
    const raw = e[field];
    const v = typeof raw === "object" && raw !== null ? (raw as { detail?: unknown }).detail : raw;
    if (typeof v === "string" && v.trim()) parts.push(`<b>${label}</b> ${v}`);
  }
  return parts.join("<br>");
}

/** A compendium entry's `actions[]`, each as `<b>NAME</b> detail`. */
function actionsProse(entry: unknown): string {
  const actions = (entry as { actions?: unknown } | undefined)?.actions;
  if (!Array.isArray(actions)) return "";
  return actions
    .map((a: { name?: unknown; detail?: unknown }) => (typeof a?.detail === "string" && a.detail.trim() ? `${typeof a.name === "string" ? `<b>${a.name.toUpperCase()}</b> ` : ""}${a.detail}` : ""))
    .filter((p) => p !== "")
    .join("<br>");
}

/**
 * A compendium entry's `deployables[]`, each as `<b>NAME</b> detail`. A deploy-only NPC System
 * (Razor Swarms, Mobile Printer) keeps its whole rule here and nowhere else.
 */
function deployablesProse(entry: unknown): string {
  const deployables = (entry as { deployables?: unknown } | undefined)?.deployables;
  if (!Array.isArray(deployables)) return "";
  return deployables
    .map((d: { name?: unknown; detail?: unknown }) => (typeof d?.detail === "string" && d.detail.trim() ? `${typeof d.name === "string" ? `<b>${d.name.toUpperCase()}</b> ` : ""}${d.detail}` : ""))
    .filter((p) => p !== "")
    .join("<br>");
}

/** Each tag's rule, `<b>TAG</b> rule`, for the tags the compendium explains. */
function tagsProse(compendium: Pick<Compendium, "getById">, tags: readonly { id: string; val?: number }[] | undefined): string {
  return (tags ?? [])
    .map((t) => {
      const rule = tagRuleText(compendium, t.id, t.val);
      return rule ? `<b>${formatTagChip(t.id, t.val)}</b> ${rule}` : "";
    })
    .filter((p) => p !== "")
    .join("<br>");
}

const joinProse = (...parts: string[]): string | undefined => parts.filter((p) => p !== "").join("<br>") || undefined;

/** A weapon's meta line, `Range 10 · Threat 1 · 2d6 KIN`, with an NPC weapon's attack bonus before the damage. */
function weaponMeta(w: WeaponProfile): string {
  const bonus = w.attackBonus !== undefined ? `${w.attackBonus >= 0 ? "+" : ""}${w.attackBonus}` : "";
  return [w.range && w.range.length > 0 ? formatWeaponRange(w.range) : "", bonus, formatWeaponDamage(w.damage)].filter((p) => p !== "").join(" · ");
}

/** A mech's weapons in mount order, then any not on a mount (frame-integrated). */
function weaponsInMountOrder(c: Combatant): WeaponProfile[] {
  const weapons = c.base.weapons ?? [];
  const byKey = new Map(weapons.map((w) => [weaponInstanceKey(w), w]));
  const out: WeaponProfile[] = [];
  for (const m of c.base.mounts ?? []) {
    for (const s of m.slots) {
      const w = byKey.get(s.weaponInstanceId);
      if (w && !out.includes(w)) out.push(w);
    }
  }
  return [...out, ...weapons.filter((w) => !out.includes(w))];
}

const ROMAN = ["", "I", "II", "III"];

type RawCore = { name?: unknown; active_name?: unknown; active_effect?: unknown; passive_name?: unknown; passive_effect?: unknown };
type RawTrait = { name?: unknown; description?: unknown };
const str = (v: unknown): string => (typeof v === "string" ? v : "");

/** A mech weapon's row as the frozen profile and the compendium state it: its mount and type, tags, meta and rule text, no live state. */
/**
 * Chips for what the freeze sets on a weapon beyond its tags: the Engineer's prototype settings
 * (`applyPrototypeConfig`, `ll1-features` 8.6) — Tweaked Optics' Accuracy, Tweaked Computer's
 * Smart, Stripped Reactor Shielding's area profiles.
 */
function frozenSettingChips(w: WeaponProfile): LoadoutChip[] {
  const tagged = (id: string) => (w.tags ?? []).some((t) => t.id === id);
  return [
    ...((w.accuracy ?? 0) > 0 && !tagged("tg_accurate") ? [{ label: `+${w.accuracy} ACCURACY`, tone: "tag" as const }] : []),
    ...(w.smart && !tagged("tg_smart") ? [{ label: "SMART", tone: "tag" as const }] : []),
    ...((w.profiles ?? []).some((p) => p.id.includes(":shielding_")) ? [{ label: "SHIELDING · +2 HEAT", tone: "tag" as const }] : []),
  ];
}

export function mechWeaponRow(w: WeaponProfile, compendium: Pick<Compendium, "getById">): LoadoutRow {
  const entry = compendium.getById("weapon", w.id);
  return {
    key: weaponInstanceKey(w),
    kind: "WPN",
    name: w.name,
    meta: weaponMeta(w),
    chips: [...(w.mount || w.type ? [{ label: [w.mount, w.type].filter(Boolean).join(" ").toUpperCase(), tone: "tag" as const }] : []), ...tagChips(w.tags), ...frozenSettingChips(w)],
    text: joinProse(entryProse(entry) || str(w.effect), tagsProse(compendium, w.tags)),
  };
}

/** A mech system's row: its SP, tags and rule text, no live state. */
export function mechSystemRow(s: FrozenSystem, compendium: Pick<Compendium, "getById">): LoadoutRow {
  const entry = compendium.getById("system", s.id);
  return {
    key: s.instanceId,
    kind: "SYS",
    name: s.name,
    ...(entry?.sp !== undefined ? { meta: `${entry.sp} SP` } : {}),
    chips: tagChips(s.tags),
    text: joinProse(entryProse(entry), actionsProse(entry)),
  };
}

/**
 * The CORE · TALENTS · TRAITS rows: the frame's core system, the pilot's talents and core bonuses
 * (`pilotEffectIds`: each talent rank `t_x:1..N` and each core bonus id) and the frame's traits.
 * `chipsFor` adds a row's live state from the affordance keys it minted; the build page passes none.
 */
export function mechFeatureRows(args: {
  frameId: string | undefined;
  pilotEffectIds: readonly string[];
  compendium: Pick<Compendium, "getById">;
  chipsFor?: (keys: readonly string[]) => LoadoutChip[];
}): LoadoutRow[] {
  const { compendium } = args;
  const chipsFor = args.chipsFor ?? (() => []);
  const frame = args.frameId ? (compendium.getById("frame", args.frameId) as Record<string, unknown> | undefined) : undefined;
  const traitEntries = frameTraitEffectEntries(frame);
  const coreEntry = frameEffectEntries(frame)[traitEntries.length];
  const core = frame?.["core_system"] as RawCore | undefined;
  const coreRows: LoadoutRow[] =
    core && str(core.name)
      ? [
          {
            key: coreEntry?.id ?? "core",
            kind: "CORE",
            name: str(core.name),
            ...(str(core.active_name) ? { meta: str(core.active_name).toUpperCase() } : {}),
            chips: coreEntry ? chipsFor([coreEntry.id]) : [],
            text: joinProse(
              str(core.passive_effect) ? `${str(core.passive_name) ? `<b>${str(core.passive_name).toUpperCase()}</b> ` : ""}${str(core.passive_effect)}` : "",
              str(core.active_effect) ? `${str(core.active_name) ? `<b>${str(core.active_name).toUpperCase()}</b> ` : ""}${str(core.active_effect)}` : "",
            ),
          },
        ]
      : [];

  const ranks = new Map<string, number>();
  const bonusIds: string[] = [];
  for (const id of args.pilotEffectIds) {
    const colon = id.lastIndexOf(":");
    if (colon > 0 && compendium.getById("talent", id.slice(0, colon))) {
      const talentId = id.slice(0, colon);
      ranks.set(talentId, Math.max(ranks.get(talentId) ?? 0, Number(id.slice(colon + 1)) || 0));
    } else if (compendium.getById("core_bonus", id)) bonusIds.push(id);
  }
  const talents: LoadoutRow[] = [...ranks].map(([talentId, rank]) => {
    const talent = compendium.getById("talent", talentId)!;
    const rankIds = Array.from({ length: rank }, (_, i) => `${talentId}:${i + 1}`);
    return {
      key: talentId,
      kind: "TAL",
      name: `${talent.name} ${ROMAN[rank] ?? rank}`,
      chips: chipsFor(rankIds),
      text: joinProse(
        ...talent.ranks.slice(0, rank).map((r) => {
          const desc = str((r as { description?: unknown }).description);
          return desc ? `<b>${r.name.toUpperCase()}</b> ${desc}` : "";
        }),
      ),
    };
  });
  const bonuses: LoadoutRow[] = bonusIds.map((id) => {
    const cb = compendium.getById("core_bonus", id)!;
    return { key: id, kind: "CB", name: cb.name, chips: chipsFor([id]), text: joinProse(str(cb.effect)) };
  });

  const rawTraits = Array.isArray(frame?.["traits"]) ? (frame["traits"] as RawTrait[]) : [];
  const traits: LoadoutRow[] = rawTraits
    .filter((t) => str(t.name).trim())
    .map((t) => {
      const id = traitEntries.find((e) => e.name === t.name)?.id;
      return { key: id ?? str(t.name), kind: "TRT", name: str(t.name), chips: id ? chipsFor([id]) : [], text: joinProse(str(t.description)) };
    });
  return [...coreRows, ...talents, ...bonuses, ...traits];
}

/**
 * **A mech's loadout** (4.2): WEAPONS in mount order, SYSTEMS, then CORE · TALENTS · TRAITS (the
 * frame's core system, the pilot's talents and core bonuses off `base.pilotEffectIds`, and the
 * frame's traits). An empty group is left out. Each row is the shared builder's, plus its live state.
 */
export function mechLoadout(args: { combatant: Combatant; state: EncounterState; compendium: Pick<Compendium, "getById"> }): LoadoutGroup[] {
  const { combatant: c, state, compendium } = args;
  const affordances = c.base.affordances;

  const weapons: LoadoutRow[] = weaponsInMountOrder(c).map((w) => {
    const row = mechWeaponRow(w, compendium);
    return {
      ...row,
      chips: [...row.chips, ...weaponChips({ weapon: w, combatant: c, state })],
      destroyed: isItemDestroyed(c.current.resources, weaponInstanceKey(w)),
      preview: weaponPreview(c, w),
    };
  });

  const systems: LoadoutRow[] = (c.base.equippedSystems ?? []).map((s) => {
    const row = mechSystemRow(s, compendium);
    const destroyed = isItemDestroyed(c.current.resources, s.instanceId);
    return {
      ...row,
      chips: [...row.chips, ...(destroyed ? [DESTROYED] : featureChips(firstState(c, state, affordanceKeysOf(affordances, s.id))))],
      destroyed,
      ...withPreview(grantedPreview(c, affordanceKeysOf(affordances, s.id))),
    };
  });

  const features = mechFeatureRows({
    frameId: c.sourceRef,
    pilotEffectIds: c.base.pilotEffectIds ?? [],
    compendium,
    chipsFor: (keys) => featureChips(firstState(c, state, keys)),
  });

  return [
    { id: "weapons", title: "WEAPONS", rows: weapons },
    { id: "systems", title: "SYSTEMS", rows: systems },
    { id: "core", title: "CORE · TALENTS · TRAITS", rows: features },
  ].filter((g) => g.rows.length > 0);
}

type RawPilotGear = { name?: unknown; type?: unknown; description?: unknown; effect?: unknown; tags?: { id: string; val?: number }[] };

/** A pilot gear weapon's row: tags, meta and rule text, no live state. */
export function pilotWeaponRow(w: WeaponProfile, compendium: Pick<Compendium, "getById">): LoadoutRow {
  const entry = compendium.getById("pilot_gear", w.id) as RawPilotGear | undefined;
  return {
    key: weaponInstanceKey(w),
    kind: "WPN",
    name: w.name,
    meta: weaponMeta(w),
    chips: tagChips(w.tags),
    text: joinProse(str(entry?.description), str(entry?.effect) || str(w.effect), tagsProse(compendium, w.tags)),
  };
}

/**
 * A pilot's ARMOR and GEAR rows off its `gear` ids (weapons come off the frozen profiles instead).
 * `extend` adds a row's live state from the gear id; the build page passes none.
 */
export function pilotGearRows(args: {
  gearIds: readonly string[];
  compendium: Pick<Compendium, "getById">;
  extend?: (id: string) => Partial<LoadoutRow>;
}): { armor: LoadoutRow[]; gear: LoadoutRow[] } {
  const { gearIds, compendium } = args;
  const armor: LoadoutRow[] = [];
  const gear: LoadoutRow[] = [];
  gearIds.forEach((id, i) => {
    const entry = compendium.getById("pilot_gear", id) as (RawPilotGear & Record<string, unknown>) | undefined;
    const type = str(entry?.type).toLowerCase();
    if (!entry || type === "weapon") return;
    const row: LoadoutRow = {
      key: `${id}:${i}`,
      kind: type === "armor" ? "ARMOR" : "GEAR",
      name: str(entry.name) || id,
      chips: tagChips(entry.tags),
      text: joinProse(str(entry.description), str(entry.effect), actionsProse(entry), tagsProse(compendium, entry.tags)),
    };
    const extra = args.extend?.(id) ?? {};
    (type === "armor" ? armor : gear).push({ ...row, ...extra, chips: [...row.chips, ...(extra.chips ?? [])] });
  });
  return { armor, gear };
}

/**
 * **A pilot's loadout on foot** (7.1): WEAPONS (the gear weapons frozen on the base, each with the
 * menu's weapon state), then ARMOR and GEAR. A gear row's uses read `featureState` over the
 * affordances the gear minted (Patch's `1 OF 1 LEFT`), as the menu's Gear rows do.
 *
 * The base freezes only the gear weapons and the gear's affordances, not the whole kit (a
 * hardsuit mints neither), so the armor and gear rows come from the roster pilot's `gear` list,
 * which the caller reads through `sourceRef`, as the mech sheet reads talents off the frozen
 * base. A pilot the roster no longer has shows its weapons only.
 */
export function pilotLoadout(args: { combatant: Combatant; state: EncounterState; compendium: Pick<Compendium, "getById">; gearIds: readonly string[] }): LoadoutGroup[] {
  const { combatant: c, state, compendium, gearIds } = args;
  const weapons: LoadoutRow[] = (c.base.weapons ?? []).map((w) => {
    const row = pilotWeaponRow(w, compendium);
    return {
      ...row,
      chips: [...row.chips, ...weaponChips({ weapon: w, combatant: c, state })],
      destroyed: isItemDestroyed(c.current.resources, weaponInstanceKey(w)),
      preview: weaponPreview(c, w),
    };
  });
  const { armor, gear } = pilotGearRows({
    gearIds,
    compendium,
    extend: (id) => ({
      chips: featureChips(firstState(c, state, affordanceKeysOf(c.base.affordances, id))),
      ...withPreview(grantedPreview(c, affordanceKeysOf(c.base.affordances, id))),
    }),
  });

  return [
    { id: "weapons", title: "WEAPONS", rows: weapons },
    { id: "armor", title: "ARMOR", rows: armor },
    { id: "gear", title: "GEAR", rows: gear },
  ].filter((g) => g.rows.length > 0);
}

/** An NPC weapon's row as the frozen profile and the compendium state it: no live state. */
function npcWeaponRow(w: WeaponProfile, compendium: Compendium): LoadoutRow {
  const entry = compendium.getById("npc_feature", w.id);
  return {
    key: weaponInstanceKey(w),
    kind: "WPN",
    name: w.name,
    meta: weaponMeta(w),
    chips: [{ label: npcWeaponLine(compendium, w).toUpperCase(), tone: "tag" }, ...tagChips(w.tags)],
    text: joinProse(entryProse(entry) || str(w.effect), tagsProse(compendium, w.tags)),
  };
}

/** An NPC feature's row as the frozen feature and the compendium state it: its kind tag, tags and rule text, no live state. */
function npcFeatureRow(f: FrozenNpcFeature, compendium: Compendium): LoadoutRow {
  const entry = f.id ? compendium.getById("npc_feature", f.id) : undefined;
  return {
    key: f.id ?? f.name,
    kind: npcFeatureKindTag(f.type),
    name: f.name,
    chips: tagChips(f.tags),
    text: joinProse(entryProse(entry) || str(f.effect), deployablesProse(entry), tagsProse(compendium, f.tags)),
  };
}

/** The ABOUT row: the class flavor and each template's text. Public at every disclosure tier (RAW p.283). */
function npcAboutRows(c: { sourceRef?: string; templateIds?: readonly string[] }, compendium: Compendium): LoadoutRow[] {
  const npcClass = c.sourceRef ? compendium.getById("npc_class", c.sourceRef) : undefined;
  const templates = (c.templateIds ?? []).map((id) => compendium.getById("npc_template", id)).filter((t) => t !== undefined);
  const about = joinProse(str(npcClass?.info?.flavor), ...templates.map((t) => (t.description ? `<b>${t.name.toUpperCase()}</b> ${t.description}` : "")));
  return about ? [{ key: "about", kind: "ABOUT", name: [npcClass?.name, ...templates.map((t) => t.name)].filter(Boolean).join(" · "), chips: [], text: about }] : [];
}

/**
 * **An NPC's loadout** (4.3), for the GM and for a player who controls it unmasked: WEAPONS, then
 * TRAITS · SYSTEMS · TECH, then an ABOUT row with the class flavor and the templates' text. The
 * NPC's reaction features come back apart, for the REACTIONS group (4.4). Every row adds its live
 * state: `featureState`'s pills, DESTROYED, and the "GM adjudicates" marker.
 */
export function npcLoadout(args: { combatant: Combatant; state: EncounterState; compendium: Compendium; overlay: LoadedOverlay; loadingOverlay: boolean }): NpcLoadout {
  const { combatant: c, state, compendium, overlay, loadingOverlay } = args;
  const weapons: LoadoutRow[] = (c.base.weapons ?? []).map((w) => {
    const row = npcWeaponRow(w, compendium);
    return {
      ...row,
      chips: [...row.chips, ...weaponChips({ weapon: w, combatant: c, state })],
      destroyed: isItemDestroyed(c.current.resources, weaponInstanceKey(w)),
      // A weapon whose `effect` or on-hit rider the engine does not carry out (the Harpoon Cannon's pull).
      gmRuled: isNpcFeatureGmRuled({ feature: { id: w.id, name: w.name, type: "Weapon" }, compendium, overlay, loadingOverlay }),
      preview: weaponPreview(c, w),
    };
  });

  const rows = (fs: FrozenNpcFeature[]) =>
    fs.map((f): LoadoutRow => {
      const row = npcFeatureRow(f, compendium);
      const destroyed = isNpcFeatureDestroyed(f, c.current.resources);
      return {
        ...row,
        chips: [...row.chips, ...(destroyed ? [DESTROYED] : featureChips(withClauseCap(firstState(c, state, f.id ? affordanceKeysOf(c.base.affordances, f.id) : []), clauseCapState({ combatant: c, state, entry: f.id ? overlay[f.id] : undefined }))))],
        destroyed,
        gmRuled: isNpcFeatureGmRuled({ feature: f, compendium, overlay, loadingOverlay }),
        ...withPreview(f.id ? grantedPreview(c, affordanceKeysOf(c.base.affordances, f.id)) : undefined),
      };
    });
  const features = orderedNpcFeatures(c.base.npcFeatures);
  const others = rows(features.filter((f) => f.type !== "Reaction"));
  const reactions = rows(features.filter((f) => f.type === "Reaction"));

  return {
    groups: [
      { id: "weapons", title: "WEAPONS", rows: weapons },
      { id: "features", title: "TRAITS · SYSTEMS · TECH", rows: [...others, ...npcAboutRows(c, compendium)] },
    ].filter((g) => g.rows.length > 0),
    reactions,
  };
}

/**
 * **A player's view of a hostile's loadout** (5.1): the rows the disclosure tier reveals, as the
 * frozen profile states them, with no live state. The caller passes what the projection carries
 * (`weapons` and `features` are empty at the `"class"` tier) and never re-derives a tier. WEAPONS,
 * then TRAITS · SYSTEMS · TECH with the ABOUT row (only ABOUT at the `"class"` tier), then
 * REACTIONS, which a player reads rather than takes, so they are an ordinary group here.
 *
 * Why no state pills, even at `"full"`: the GM's pills read `featureState` and `current.resources`
 * off a whole `Combatant`, and a player's `"full"` view is an `HpHiddenCombatant` whenever
 * `showEnemyHp` is off. Pills that came and went with the HP policy would be a second, accidental
 * disclosure axis.
 */
export function dossierLoadout(args: {
  combatant: { id?: string; sourceRef?: string; templateIds?: readonly string[] };
  weapons: readonly WeaponProfile[];
  features: readonly FrozenNpcFeature[];
  compendium: Compendium;
}): LoadoutGroup[] {
  const { combatant, weapons, features, compendium } = args;
  const ordered = orderedNpcFeatures(features);
  const others = ordered.filter((f) => f.type !== "Reaction").map((f) => npcFeatureRow(f, compendium));
  return [
    // A revealed weapon previews its reach while hovered, as the GM's row does.
    { id: "weapons", title: "WEAPONS", rows: weapons.map((w) => ({ ...npcWeaponRow(w, compendium), ...(combatant.id !== undefined ? { preview: weaponPreview({ id: combatant.id }, w) } : {}) })) },
    { id: "features", title: others.length > 0 ? "TRAITS · SYSTEMS · TECH" : "ABOUT", rows: [...others, ...npcAboutRows(combatant, compendium)] },
    { id: "reactions", title: "REACTIONS", rows: ordered.filter((f) => f.type === "Reaction").map((f) => npcFeatureRow(f, compendium)) },
  ].filter((g) => g.rows.length > 0);
}
